import React from "react";
// import { ProjectFailure } from "../../../../api/v1/failure/ProjectFailure";
import t from "../../../../i18n";
import type { ColumnMetaData } from "@ui/Table/ColumnMetaData";
import ReparabilityTag from "./Reparability";
import type { Column } from "./useColumns";
import { useColumns, COLUMNS } from "./useColumns";
import formatNumber from "@ui/i18n/formatNumber";
import type { ClassificationFailure } from "../../../../api/useClassificationFailures";
import { useTranslations } from "../../../../api/useTranslations";

const useMetadata = ({ displayManualFailures }: { displayManualFailures: boolean }) => {
  const translations = useTranslations();
  const [visibleColumns] = useColumns({});

  const deferredVisibleColumns = React.useDeferredValue(visibleColumns);
  const hidden = React.useCallback(
    (column: Column) => !deferredVisibleColumns.includes(column),
    [deferredVisibleColumns],
  );

  const tableMetadata = React.useMemo(() => {
    const baseMetadata: {
      [key in Exclude<Column, "reparability">]: ColumnMetaData<ClassificationFailure>;
    } = {
      displayName: {
        label: t("setup.paragraphs.quantities.table.definitions.column.name"),
        render: q => ("displayName" in q ? q.displayName : q.name), // Manual failures have no displayName
        minWidth: "250px",
        sticky: true,
        hidden: hidden("displayName"),
      },
      technicalName: {
        label: t("setup.paragraphs.quantities.table.definitions.column.technicalName"),
        render: q => q.name,
        minWidth: "250px",
        hidden: hidden("technicalName"),
      },
      projectName: {
        label: t("administration.quantities.table.header.project_name"),
        render: q => ("project" in q ? q.project.name : q.setup?.project.name),
        minWidth: "200px",
        ellipsis: true,
        hidden: hidden("projectName"),
      },
      projectType: {
        label: t("administration.quantities.table.header.project_type", translations),
        render: q => ("project" in q ? q.project.typeId : q.setup?.project.typeId),
        minWidth: "200px",
        ellipsis: true,
        hidden: hidden("projectType"),
      },
      projectSite: {
        label: t("administration.quantities.table.header.project_site"),
        minWidth: "200px",
        render: q => ("project" in q ? q.project.siteId : q.setup?.project.siteId),
        hidden: hidden("projectSite"),
      },
      setupName: {
        label: t("administration.quantities.table.header.project_setup"),
        render: q => ("setup" in q ? q.setup.name : undefined),
        minWidth: "200px",
        hidden: hidden("setupName"),
      },
      description: {
        label: t("setup.paragraphs.quantities.table.definitions.column.description"),
        render: q => ("setup" in q ? q.description : undefined),
        minWidth: "200px",
        hidden: hidden("description"),
      },
      unit: {
        label: t("setup.paragraphs.quantities.table.definitions.column.unit"),
        render: q => ("setup" in q ? q.unit : undefined),
        minWidth: "90px",
        hidden: hidden("unit"),
      },
      degradationType: {
        label: t("administration.failures.table.header.degradation_type"),
        minWidth: "250px",
        render: q => q.degradationTypeId ?? undefined,
        hidden: hidden("degradationType"),
      },
      category: {
        label: t("administration.failures.table.header.category"),
        minWidth: "150px",
        render: q => q.failureCategoryId ?? undefined,
        hidden: hidden("category"),
      },
      system: {
        label: t("setup.paragraphs.quantities.table.definitions.column.system"),
        render: q => q.systemId,
        minWidth: "250px",
        hidden: hidden("system"),
      },
      equipmentType: {
        label: t("setup.paragraphs.quantities.table.definitions.column.equipmentType"),
        render: q => q.equipmentTypeId,
        minWidth: "250px",
        hidden: hidden("equipmentType"),
      },
      min: {
        label: t("administration.failures.table.header.min"),
        minWidth: "100px",
        render: q => ("min" in q ? formatNumber(q.min, { precision: 2 }) : undefined),
        hidden: hidden("min"),
      },
      max: {
        label: t("administration.failures.table.header.max"),
        minWidth: "100px",
        render: q => ("max" in q ? formatNumber(q.max, { precision: 2 }) : undefined),
        hidden: hidden("max"),
      },
      magnitude: {
        label: t("administration.failures.table.header.magnitude"),
        minWidth: "100px",
        render: q => ("amplitude" in q ? formatNumber(q.amplitude, { precision: 2 }) : undefined),
        hidden: hidden("magnitude"),
      },
      inputModelName: {
        label: t("setup.paragraphs.quantities.table.definitions.column.inputModelName"),
        minWidth: "340px",
        render: q =>
          q && "setup" in q
            ? (q.setup.simulationInputs.find(({ id }: { id: string }) => q.id === id)?.name ??
              undefined)
            : undefined,
        hidden: hidden("inputModelName"),
      },
    };

    const dynamicMetadata: { [key in Column | string]: ColumnMetaData<ClassificationFailure> } = {
      ...baseMetadata,
    };
    if (!displayManualFailures) {
      dynamicMetadata["reparability"] = {
        label: t("administration.failures.table.header.reparability"),
        minWidth: "250px",
        render: q =>
          "reparability" in q && q.reparability !== null ? (
            <ReparabilityTag reparability={q.reparability} />
          ) : undefined,
        hidden: hidden("reparability"),
      };
    }

    const columnsOrder = [...COLUMNS]; // Supposons que vous avez un tableau de noms de colonnes
    const dynamicOrderedMetadata: {
      [key in Column | string]: ColumnMetaData<ClassificationFailure>;
    } = {};
    for (const column of columnsOrder) {
      if (column in dynamicMetadata && dynamicMetadata[column] !== undefined) {
        dynamicOrderedMetadata[column] = dynamicMetadata[
          column
        ] as ColumnMetaData<ClassificationFailure>;
      }
    }

    return dynamicOrderedMetadata;
  }, [hidden, translations, displayManualFailures]);
  return tableMetadata;
};

export default useMetadata;
